import { MAX_REPORTING_RANGE_DAYS } from "@adpilot/shared";
import { IconCalendar, IconChevronDown } from "@tabler/icons-react";
import { addDays, format, parseISO, subDays } from "date-fns";
import { enUS, zhCN } from "date-fns/locale";
import { useState } from "react";
import type { DateRange, Matcher } from "react-day-picker";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverTitle,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { type ReportingTimeZone, reportingDateInTimeZone } from "@/lib/reporting-time-zone";
import { cn } from "@/lib/utils";
import {
  REPORTING_PERIOD_PRESETS,
  type ReportingPeriod,
  type ReportingPeriodPreset,
  resolveReportingPeriod,
} from "@/stores/reporting-period";

interface ReportingPeriodSelectProps {
  value: ReportingPeriod;
  timeZone: ReportingTimeZone;
  onValueChange: (value: ReportingPeriod) => void;
}

function toDateRange(period: ReportingPeriod, timeZone: ReportingTimeZone): DateRange {
  const resolved = resolveReportingPeriod(period, new Date(), timeZone);
  return { from: parseISO(resolved.startDate), to: parseISO(resolved.endDate) };
}

function toIsoDate(date: Date): string {
  return format(date, "yyyy-MM-dd");
}

/**
 * Days the calendar must grey out so a selection can never exceed the span the
 * API accepts. Without this the picker happily builds an over-long window and
 * every panel drops to ErrorState on the resulting 422, with no hint as to why.
 *
 * Deliberately three independent matchers rather than one `{ before, after }`:
 * react-day-picker reads that pair as a DateInterval whose meaning flips on
 * operand order (`isAfter(before, after)` picks "inside" vs "outside"), so a
 * combined matcher would silently stop disabling anything if the operands ever
 * crossed — with no type error and no failing render to catch it.
 */
export function outOfRangeDays(anchor: Date | undefined, today: Date): Matcher[] {
  if (!anchor) return [{ after: today }];

  const span = MAX_REPORTING_RANGE_DAYS - 1;
  return [{ before: subDays(anchor, span) }, { after: today }, { after: addDays(anchor, span) }];
}

export function ReportingPeriodSelect({
  value,
  timeZone,
  onValueChange,
}: ReportingPeriodSelectProps): React.ReactElement {
  const { t, i18n } = useTranslation("layout");
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateRange>(() => toDateRange(value, timeZone));
  const [draftPreset, setDraftPreset] = useState<ReportingPeriodPreset | null>(() =>
    value.type === "preset" ? value.value : null,
  );
  const locale = i18n.resolvedLanguage === "zh-CN" ? zhCN : enUS;
  const today = parseISO(reportingDateInTimeZone(new Date(), timeZone));

  const presetLabels: Record<ReportingPeriodPreset, string> = {
    yesterday: t("reportingPeriod.yesterday"),
    "7d": t("reportingPeriod.last7Days"),
    "14d": t("reportingPeriod.last14Days"),
    "30d": t("reportingPeriod.last30Days"),
  };

  function resetDraft(): void {
    setDraft(toDateRange(value, timeZone));
    setDraftPreset(value.type === "preset" ? value.value : null);
  }

  function handleOpenChange(nextOpen: boolean): void {
    if (nextOpen) resetDraft();
    setOpen(nextOpen);
  }

  function selectPreset(preset: ReportingPeriodPreset): void {
    setDraft(toDateRange({ type: "preset", value: preset }, timeZone));
    setDraftPreset(preset);
  }

  function applyDraft(): void {
    if (!draft.from || !draft.to) return;

    onValueChange(
      draftPreset
        ? { type: "preset", value: draftPreset }
        : { type: "custom", startDate: toIsoDate(draft.from), endDate: toIsoDate(draft.to) },
    );
    setOpen(false);
  }

  function selectCalendarDay(day: Date): void {
    setDraftPreset(null);
    if (!draft.from || draft.to || day < draft.from) {
      setDraft({ from: day, to: undefined });
      return;
    }

    setDraft({ from: draft.from, to: day });
  }

  const selectingEnd = Boolean(draft.from && !draft.to);

  // While an end date is still pending, the start date anchors the allowed span.
  const disabledDays = outOfRangeDays(draft.from && !draft.to ? draft.from : undefined, today);

  const appliedRange = toDateRange(value, timeZone);
  const displayFormat = new Intl.DateTimeFormat(i18n.resolvedLanguage, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const formatBoundary = (date: Date, boundary: "start" | "end"): string =>
    `${displayFormat.format(date)} ${boundary === "start" ? "00:00:00" : "23:59:59"}`;
  const appliedLabel =
    appliedRange.from && appliedRange.to
      ? `${displayFormat.format(appliedRange.from)} – ${displayFormat.format(appliedRange.to)}`
      : t("reportingPeriod.title");
  const triggerLabel = value.type === "preset" ? presetLabels[value.value] : appliedLabel;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger
        render={
          <Button
            variant="outline"
            size="sm"
            aria-label={`${t("reportingPeriod.title")}: ${appliedLabel}`}
            title={appliedLabel}
            className="w-[140px] justify-between"
          />
        }
      >
        <IconCalendar data-icon="inline-start" />
        <span
          className={cn("min-w-0 truncate", value.type === "custom" && "font-mono tabular-nums")}
        >
          {triggerLabel}
        </span>
        <IconChevronDown data-icon="inline-end" className="ml-auto" />
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[760px] overflow-hidden rounded-[14px] p-0"
      >
        <div className="grid grid-cols-[156px_1px_minmax(0,1fr)]">
          <aside className="flex flex-col p-3">
            <span className="px-2 pb-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              {t("reportingPeriod.quickSelect")}
            </span>
            <div className="flex flex-col gap-1">
              {REPORTING_PERIOD_PRESETS.map((preset) => (
                <Button
                  key={preset}
                  type="button"
                  variant={draftPreset === preset ? "secondary" : "ghost"}
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => selectPreset(preset)}
                >
                  {presetLabels[preset]}
                </Button>
              ))}
            </div>
          </aside>

          <Separator orientation="vertical" />

          <section className="min-w-0">
            <div className="flex items-center justify-between px-5 pb-3 pt-4">
              <div>
                <PopoverTitle className="text-[14px]">
                  {t("reportingPeriod.currentPeriod")}
                </PopoverTitle>
                <PopoverDescription className="mt-1 text-[11px]">
                  {t("reportingPeriod.selectHint")}
                </PopoverDescription>
              </div>
              <div className="grid grid-cols-[132px_16px_132px] items-center">
                <DateValue
                  label={t("reportingPeriod.startDate")}
                  value={draft.from ? formatBoundary(draft.from, "start") : "—"}
                  active={!selectingEnd}
                />
                <span className="text-center text-muted-foreground">–</span>
                <DateValue
                  label={t("reportingPeriod.endDate")}
                  value={draft.to ? formatBoundary(draft.to, "end") : "—"}
                  active={selectingEnd}
                />
              </div>
            </div>

            <Separator />

            <Calendar
              mode="range"
              selected={draft}
              // onSelect keeps DayPicker controlled; its default range algorithm
              // must not maintain a second selection alongside our two-click draft.
              onSelect={(_range, day, modifiers) => {
                if (!modifiers.disabled) selectCalendarDay(day);
              }}
              numberOfMonths={2}
              defaultMonth={draft.from}
              disabled={disabledDays}
              locale={locale}
              className="mx-auto p-4 [--cell-size:--spacing(9)]"
              classNames={{ months: "gap-7", month: "w-[252px]" }}
            />

            <Separator />

            <footer className="flex items-center justify-end gap-2 px-4 py-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleOpenChange(false)}
              >
                {t("reportingPeriod.cancel")}
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={!draft.from || !draft.to}
                onClick={applyDraft}
              >
                {t("reportingPeriod.apply")}
              </Button>
            </footer>
          </section>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function DateValue({
  label,
  value,
  active,
}: {
  label: string;
  value: string;
  active: boolean;
}): React.ReactElement {
  return (
    <fieldset
      aria-label={label}
      aria-current={active ? "step" : undefined}
      className={cn(
        "m-0 flex h-14 min-w-0 flex-col justify-center rounded-[8px] border border-control-border bg-control-default px-2.5",
        active && "border-ring ring-1 ring-ring",
      )}
    >
      <span className="text-[9px] leading-3 text-muted-foreground">{label}</span>
      <span className="font-mono text-[11px] leading-4 tabular-nums text-ink">{value}</span>
    </fieldset>
  );
}
